import React, { useState } from 'react';
import { ApolloQueryResult } from '@apollo/client';
import Button, { ButtonGroup, LoadingButton } from '@atlaskit/button';
import { Checkbox } from '@atlaskit/checkbox';
import Form, { FormApi, FormFooter, FormHeader, FormSection } from '@atlaskit/form';
import defaultTo from 'lodash/defaultTo';
import get from 'lodash/get';
import set from 'lodash/set';
import { UpdateBillToAndShipToParties } from '@atlassian/cssxp-types-billing';
import { CommerceAddress } from '@css-xp/graphtypes';
import {
  useSidebar,
  InfoPanel,
  useFormSubmit,
  AddressFormSection,
  ErrorComponents,
  useNotification,
  successNotificationBuilder,
} from '@css-xp/base';
import { updateShipToParty } from '@atlassian/temp-proxies';
// import { ENABLE_SAME_AS_BILL_TO } from '@css-xp/types-featureflag';
// import { getFeatureFlag } from '../common/security/selectors';
// import { useSelector } from 'react-redux';
import ChangePricingEligibility from '../component/update-pricing-eligibility';

interface FormData {
  pricingEligibility?: string;
  shipToName?: string;
  shipToAddress1?: string | null;
  shipToAddress2?: string | null;
  shipToCity?: string | null;
  shipToCountry?: { value: string; label: string };
  shipToPostalCode?: string | null;
  shipToState?: { value: string; label: string };
  shipToTaxId?: string | null;
}
interface Props {
  transactionAccountId: string;
  pricingEligibility: string | null;
  shipToAddress?: CommerceAddress;
  billToAddress?: CommerceAddress;
  refetch?: () => Promise<ApolloQueryResult<unknown>>;
}

export const EditShipToPartySidebar = ({
  transactionAccountId,
  shipToAddress,
  billToAddress,
  pricingEligibility,
  refetch,
}: Props) => {
  const { closeSidebar } = useSidebar();

  const { error: formError, isProcessing, submitForm, complete } = useFormSubmit<UpdateBillToAndShipToParties, any>({
    submitFormAction: updateShipToParty,
    onComplete: () => {
      closeSidebar();
      if (refetch) {
        refetch();
      }
    },
  });

  const [isChecked, setIsChecked] = useState(false);
  //set ship to as bill to if checkbox is selected
  const newshipToAddress = isChecked ? billToAddress : shipToAddress;

  // const sameAsBillToCheckBoxFeatureFlag = useSelector(
  //   getFeatureFlag(ENABLE_SAME_AS_BILL_TO)
  // );
  const onSameAsBillTo = () => {
    setIsChecked(!isChecked);
  };

  const submitEditBillToPartyDetails = (billingData: FormData, formApi: FormApi<FormData>) => {
    // @atlaskit/form does not handle default form values well, creating thing like `selectedAddOns[0].selectedAddOn` instead of creating an object at that index. Lodash has our back.
    const transformedData: FormData = {};
    // setting key to any as getFieldState uses the actual keys of FormData, which does not include array indices
    formApi.getRegisteredFields().forEach((key: any) => {
      // get the value from the field state or the form data
      const value = formApi.getFieldState(key)?.value ?? get(billingData, key);
      set(transformedData, key, value);
    });

    const updateBillToPartyPayload: UpdateBillToAndShipToParties = {
      pricingEligibility: {
        [get(transformedData, 'pricingPlan')]: 'true',
      },
      transactionAccountId,
      shipToAddress: {
        id: shipToAddress?.id ?? null,
        name: defaultTo(transformedData.shipToName || null, ''),
        postalAddress: {
          line1: defaultTo(transformedData.shipToAddress1 || null, ''),
          line2: defaultTo(transformedData.shipToAddress2 || null, null),
          city: transformedData?.shipToCity ?? '',
          state: defaultTo(transformedData?.shipToState?.value || null, null),
          postcode: transformedData?.shipToPostalCode ?? '',
          country: transformedData?.shipToCountry?.value ?? '',
        },
        taxId: transformedData?.shipToTaxId || null,
      },
    };
    submitForm(updateBillToPartyPayload);
  };

  const { sendSuccessNotification, notificationSent, setNotificationSent } = useNotification();
  if (complete && !notificationSent) {
    const notificationOptions = successNotificationBuilder()
      .withId('update-shipTo-party-success')
      .withMessage(`Successfully ${!shipToAddress ? 'created' : 'updated'} ship to party.`)
      .withDismissAfter(5000)
      .build();
    sendSuccessNotification(notificationOptions);
    setNotificationSent(true);
  }

  const title = !shipToAddress ? 'Create ship to party' : 'Update ship to party';

  return (
    <div style={{ maxWidth: '500px', padding: '25px' }}>
      <Form onSubmit={submitEditBillToPartyDetails}>
        {({ formProps, submitting, setFieldValue }) => (
          <form {...formProps}>
            <FormHeader title={title}></FormHeader>
            <FormSection>
              {formError && (
                <InfoPanel
                  type="error"
                  message={
                    <ErrorComponents.ErrorMessageOnInfoPanel
                      title="We're unable to create a new invoice group for the following reason"
                      message={formError}
                    />
                  }
                />
              )}
            </FormSection>

            <FormSection>
              <h3>Ship to address</h3>
              {
                /*sameAsBillToCheckBoxFeatureFlag && */ <Checkbox
                  isChecked={isChecked}
                  onChange={onSameAsBillTo}
                  label="Same as bill to address"
                  value="Controlled Checkbox"
                  name="controlled-checkbox"
                />
              }
              <AddressFormSection
                {...newshipToAddress}
                isEditable={true}
                addressName="Ship to"
                setFieldValue={setFieldValue}
                isRequired={true}
              />
            </FormSection>
            <ChangePricingEligibility pricingEligibility={pricingEligibility} />
            <FormFooter align="end">
              <ButtonGroup>
                <LoadingButton
                  appearance="primary"
                  type="submit"
                  isLoading={submitting || isProcessing}
                  isDisabled={false}
                  testId={submitting || isProcessing ? 'loading-button' : 'save-button'}
                >
                  Save
                </LoadingButton>
                <Button appearance="subtle" onClick={closeSidebar}>
                  Close
                </Button>
              </ButtonGroup>
            </FormFooter>
          </form>
        )}
      </Form>
    </div>
  );
};
